// AgentStudioChat.jsx — m3-13 Agent Studio chat SHELL (layout only).
//
// m3-nav-morph §1: .st-side (the chat-history sidebar) is DELETED from this component.
// The chat navigation (New chat / Search / Chats) has moved into VsNavLower.jsx, which
// mounts into #vs-nav-lower inside nav.sidebar. This component now renders ONLY the
// conversation area (hero + turns) and the composer.
//
// m-r1-08 WIRED: send → POST /api/agent/companies/:slug/chats/:chat_id/messages with
//   { message_id: crypto.randomUUID(), text, model }; subscribes to SessionChannel for
//   the chat's session and renders durable steps via the CANONICAL observeReducer
//   (pages/observeReducer.js, dedupe-by-seq + delta typewriter).
//
// Task-reference pill (board→chat handoff) is m3-15.
// §7 events (new-chat, select-chat) are dispatched by VsNavLower.jsx.

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import consumer from "../channels/consumer"
import {
  observeReducer,
  initialObserveState,
  durableEvents,
} from "../pages/observeReducer"
import {
  MODELS, DEFAULT_MODEL, resolveModel, modelById,
  persistModel, readPersistedModel,
  DEFAULT_EFFORT, resolveEffort, availableEfforts, persistEffort, readPersistedEffort,
  buildSendBody,
  canSend, composerKeyAction,
  // S-E5: send gating (canSubmit gates on `working`) + same-tick double-send latch.
  canSubmit, acquireSendLock, releaseSendLock,
  buildConversation,
  // Task 5: composer attachments — client validation + wire mapping + accept string.
  validateAttachmentFile, attachmentsToWire, hasUploadingAttachment, isImageAttachment, ATTACHMENT_ACCEPT,
} from "./studioShellViewModel.js"
// Area B: the Suggestion Dock + its combined task/job data (replaces the old .st-suggest cards).
import SuggestionDock from "./SuggestionDock.jsx"
// Chat privacy 3e — the shared sharing/visibility modal + the header glyph helper.
import ChatVisibilityModal, { visibilityGlyph } from "./ChatVisibilityModal.jsx"
import { buildDockSuggestions } from "./dockSuggestionsViewModel.js"
import { mergeTimeline, toOptimisticUserTurns, liveTurnsFromDeltas } from "./chatTimeline.js"
// S-B4: pure auto-scroll logic (stick-to-bottom only on growth-at-bottom, never yank a scrolled-up user).
import { isNearBottom, shouldStickToBottom } from "../pages/scrollState.js"
import { decideSend } from "./askVestaHandoff.js"
import { isWorking, runTerminated, awaitingApproval, awaitingApprovalRequiresCapability } from "../pages/workingState.js"
// CHAT-3: settled assistant turns render through markdown (fixes literal `**` + run-on wall).
// User bubbles + the live typewriter delta STAY plain text — shouldMarkdownRender is the gate.
import AssistantMarkdown from "./AssistantMarkdown.jsx"
// A5: live typewriter lane renders rich markdown (repaired mid-stream) instead of raw text.
import StreamingMarkdown from "./StreamingMarkdown.jsx"
// E2: animated working indicator (cycling verbs + role="status"); replaces the static dots block.
import ThinkingIndicator from "./ThinkingIndicator.jsx"
// S-H1: pure Send/Stop button-mode picker (P0-4 stop-a-run).
import { sendButtonMode } from "./sendButtonMode.js"
// S-B6: classify a failed send (busy/fatal/retryable) so the failed turn renders the right affordance.
import { classifySendError, sendFailureMessage } from "./classifySendError.js"
// S-A8: extract the RAW markdown a turn should copy (not rendered HTML).
import { copyableText } from "./copyableText.js"
import { shouldMarkdownRender } from "./renderPolicy.js"
// S-B1: thread the client message_id through the new-chat POST body so Sessions::Start ingests
// a durable TEXT_MESSAGE that dedups the optimistic first bubble (else it renders twice).
import { buildCreateChatBody } from "./createChatBody.js"
// D2: the nav-chat-created event payload (normalizes id to String).
import { chatCreatedEvent } from "./navChatList.js"
// S-D8: connection lifecycle reducer (disconnected/rejected/connected) → connectionState chip.
import { connectionReducer } from "../pages/connectionReducer.js"
import { SUGGESTIONS_TWO_COLUMN_MIN_WIDTH } from "./agentLayout.js"
// G5: in-thread home for durable CUSTOM agent.report directives.
import ReportArtifact from "./ReportArtifact.jsx"
// P4: the chat half of dual-surface approve. postData drives the SAME interactions#submit endpoint
// the board uses; buildSubmitBody builds the SAME per-viewer-capability submit body (read-only/
// non-bound → null, so the card cannot submit) — one resolution path, two entry points.
import { postData } from "../utils/api"
import { buildSubmitBody } from "../pages/interactionComponent.js"
// Item B — the pure create-campaign detail view-model (objective/name/budget/why), shared with the
// board drawer's ApprovalArtifact so the two surfaces cannot drift.
import { isCreateCampaign, createDetailRows, whyBlock, isListsendCall, listsendDetailRows } from "./approvalCreateDetails.js"
// P2 — the pure errored/retry view-model for an approve-then-apply FAILURE, shared VERBATIM with the
// board drawer so the two surfaces cannot drift on what a failed card says or which button it shows.
import { applyFailureFrom, applyFailureFromError, approveLabel, failureView } from "./approvalFailure.js"
// Item #7: library return handoff — mirror activeChat into sessionStorage (so the panel-side
// View-all click site can see it) + stage the return descriptor at this dock's own View-all click.
import { agentLibraryHref, mirrorActiveChat, readActiveChatId, stageReturn } from "./libraryReturn.js"
import { readPersistedActivityView } from "./modeResolver.js"

const safeStorage = () => { try { return window.localStorage } catch (_e) { return null } }

// P4 — the in-thread approval card, the CHAT half of "approve from EITHER surface". A durable
// interaction directive renders here; it is ACTIONABLE iff the live observe-stream serializer
// (SessionChannel#replay → Agent::InteractionEventCapability) minted a per-viewer capability for THIS
// viewer onto the event AND the interaction is not yet resolved — exactly the board's contract
// (SessionDrawer), the SAME interactions#submit endpoint, the SAME server-enforced double opt-in.
// Non-bound viewer (no token) or an already-settled card → read-only title only (buildSubmitBody
// returns null → `canSubmit` false), preserving the read-only contract for everything else.
function ChatInteractionCard({ turn, sessionId }) {
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  // Fix #2 — the per-viewer capability token, fetched on demand for a LIVE interaction card whose WS
  // envelope carried none (see the interactions#capability endpoint). fetchedCap is a bare token STRING
  // (we pick .token off the endpoint's capability object) — the SAME shape turn.capability has on the
  // replay path (chatTimeline.js reduces the durable object to a string), so buildSubmitBody gets a
  // string in BOTH paths. capState drives the on-brand loading/error affordance (never a gray button).
  const [fetchedCap, setFetchedCap] = useState(null)
  const [capState, setCapState] = useState("idle") // idle | loading | ready | error | not_bound
  // P2 — the approve-then-apply FAILURE. Fed from TWO doors carrying the SAME server stamp:
  //   * DURABLE  — turn.applyFailure (the interaction payload, merged from MoneyAction#card), so a
  //                reload/reconnect brings the errored card back exactly as it was left
  //   * IMMEDIATE — the 403 body of the approve we just POSTed, so the card changes on the click
  //                 that failed rather than waiting for a refetch that never comes (the failure path
  //                 emits NO TOOL_CALL_RESULT, so nothing else ever re-renders this card)
  // Local state WINS over the durable seed: after a fresh retry it is the newest truth.
  const [liveFailure, setLiveFailure] = useState(null)
  const applyFailure = liveFailure || applyFailureFrom(turn.applyFailure)

  const title = (turn.render && (turn.render.payload?.title || turn.render.title)) || "Approval needed"

  // Item B — the create-detail block. payload.change carries the full propose manifest
  // (kind/objective/name/cbo/daily_budget_cents); the money `summary` is hardcoded $0 → $0 for
  // creates and is NOT read. Non-create cards (pause/boost/activate/send) get null → no block.
  const cardPayload = (turn.render && turn.render.payload) || {}
  const createRows = isCreateCampaign(cardPayload.change) ? createDetailRows(cardPayload) : null
  // A ListSend card (change.kind listsend_call) renders its server-built rows in the SAME block.
  const detailRows = createRows || (isListsendCall(cardPayload.change) ? listsendDetailRows(cardPayload) : null)
  const why = detailRows ? whyBlock(cardPayload) : null

  const capPath = `api/agent/sessions/${sessionId}/interactions/capability`
  // Only fetch when the card needs a token and doesn't have one: a live requires-capability directive,
  // unresolved, with no durable token and none fetched yet.
  const needsFetch =
    Boolean(sessionId) && turn.requiresCapability && !turn.capability && !turn.resolved && !fetchedCap

  const loadCapability = useCallback(() => {
    if (!sessionId || !turn.interaction_id) return
    setCapState("loading")
    return postData(capPath, { interaction_id: turn.interaction_id })
      .then((res) => {
        // P2 — this endpoint is what a freshly-mounted chat card asks for its token, so it is also
        // how a card that failed on a PREVIOUS visit comes back up in its errored/retry state.
        const durable = applyFailureFrom(res && res.apply_failure)
        if (durable) setLiveFailure(durable)
        const cap = res && res.capability
        if (cap && cap.token) {
          setFetchedCap(cap.token)
          setCapState("ready")
          return cap.token
        }
        setCapState("ready") // resolved:true / nothing to do → no buttons, no error
        return null
      })
      .catch((err) => {
        const status = err && err.response && err.response.status
        setCapState(status === 403 ? "not_bound" : "error")
        return null
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, turn.interaction_id, capPath])

  useEffect(() => {
    if (needsFetch) loadCapability()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFetch])

  // Reconstruct the interaction record buildSubmitBody/interactionViewModel consume (the SAME shape
  // interactionComponent.reduceInteractions emits). buildSubmitBody returns null unless the directive
  // is supported, unresolved, and capability-satisfied — so it is the single gate for "can act".
  const interaction = {
    interaction_id: turn.interaction_id,
    render: turn.render,
    requiresCapability: turn.requiresCapability,
    capability: turn.capability || fetchedCap, // ← the load-bearing edit: use the fetched token if the live envelope had none
    resolved: turn.resolved,
  }
  const canApprove = Boolean(sessionId) && buildSubmitBody(interaction, { approved: true }) != null
  const submitPath = `api/agent/sessions/${sessionId}/interactions/submit`

  const errMessage = (err) =>
    (err && err.response && err.response.data && (err.response.data.reason || err.response.data.error)) ||
    (err && err.message) ||
    "Could not submit — please try again."

  // P2 — an APPLY FAILURE is not an ordinary submit error and must not be reported as one. The 403
  // carries a structured `apply_failure` stamp; when it is there we flip the card into the errored/
  // retry state (plain-language copy + attempt count) INSTEAD of dumping the raw
  // "apply_refused: funnel_failed_at_adset: …" string into the inline error line. Returns true when
  // it handled the error, so each catch below stays a one-liner.
  const captureApplyFailure = (err) => {
    const failure = applyFailureFromError(err)
    if (!failure) return false
    setLiveFailure(failure)
    setSubmitError(null)
    return true
  }

  const reportError = (err) => {
    if (!captureApplyFailure(err)) setSubmitError(errMessage(err))
  }

  // Approve = the SERVER-ENFORCED double opt-in (identical to SessionDrawer): the first POST carries
  // only the approval capability; a money card 403s `confirm_required` with a fresh short-TTL confirm
  // token → AUTO-RESUBMIT once with it. On success the Session resumes → a TOOL_CALL_RESULT broadcasts
  // → this card settles to read-only via the durable stream (no manual refetch — the chat converges).
  const handleApprove = async () => {
    const body = buildSubmitBody(interaction, { approved: true })
    if (!body || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await postData(submitPath, body)
    } catch (err) {
      const data = err && err.response && err.response.data
      const confirm = data && data.error === "confirm_required" && data.confirm_capability && data.confirm_capability.token
      // A fetched token may have expired between mint and click — re-mint once and retry the approve.
      const stale = data && data.error === "capability_rejected" && /expired|invalid|binding/.test(String(data.reason || ""))
      if (confirm) {
        try {
          await postData(submitPath, { ...body, confirm_capability: data.confirm_capability.token })
        } catch (err2) {
          // The double opt-in means the EXECUTE leg runs on this second POST — so this is the catch
          // an apply failure actually lands in.
          reportError(err2)
        }
      } else if (stale) {
        try {
          const token = await loadCapability()
          if (!token) throw err
          await postData(submitPath, { ...buildSubmitBody({ ...interaction, capability: token }, { approved: true }) })
        } catch (err3) {
          reportError(err3)
        }
      } else {
        reportError(err)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    const body = buildSubmitBody(interaction, { reason: "declined by user" })
    if (!body || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await postData(submitPath, { ...body, decision: "reject" })
    } catch (err) {
      setSubmitError(errMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  // P2 — the errored/retry view-model. Scoped to the ACTIONABLE card on purpose: once a retry
  // succeeds the interaction resolves through the durable stream, canApprove goes false and the card
  // settles to title-only read-only — no stale red box on a card that has since gone through.
  // Gated on canApprove so that when a RETRY finally succeeds — the interaction resolves through
  // the durable stream and the card settles to title-only read-only — the errored framing drops
  // with it. Otherwise a card that has since gone through would keep a red border and no
  // explanation of why.
  const failure = canApprove ? failureView(applyFailure, { submitting }) : null
  // "Retry" after a failure, "Approve" otherwise — same purple .pgs-approve either way (a primary
  // action is NEVER a gray/native button in this repo).
  const approveButtonLabel = approveLabel(applyFailure, { submitting })

  // Render states (all on-brand — never a bare/gray button):
  //   canApprove            → the purple .pgs-approve / .pgs-decline actions
  //     + applyFailure      → an errored card: plain-language failure, attempt count, "Retry"
  //   capState "loading"    → a muted "Preparing approval…" caption (NOT a disabled grey button)
  //   capState "error"      → a purple text-link retry (.st-approval-card__retry)
  //   else (not_bound/idle/resolved) → title-only read-only
  return (
    <div className={failure ? "st-approval-card st-approval-card--failed" : "st-approval-card"} role="group">
      <span className="st-approval-card__title">{title}</span>
      {detailRows && (
        <dl className="st-approval__details">
          {detailRows.map((r) => (
            <div className="st-approval__row" key={r.key}>
              <dt className="st-approval__k">{r.key}</dt>
              <dd className="st-approval__v">{r.value}</dd>
            </div>
          ))}
          {why && why.summary && (
            <div className="st-approval__row st-approval__why">
              <dt className="st-approval__k">Why</dt>
              <dd className="st-approval__v">{why.summary}</dd>
            </div>
          )}
          {why && why.details.length > 0 && (
            <ul className="st-approval__why-details">
              {why.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
        </dl>
      )}
      {canApprove ? (
        <div className="st-approval-card__actions">
          {failure && (
            <div className="st-approval-card__failure" role="status" data-testid="st-apply-failure">
              <span className="st-approval-card__failure-head">
                This didn’t go through{failure.attempt ? ` — ${failure.attempt}` : ""}
              </span>
              <span className="st-approval-card__failure-body">{failure.message}</span>
            </div>
          )}
          {submitError && <div className="st-approval-card__err">{submitError}</div>}
          <button
            className="pgs-approve"
            onClick={handleApprove}
            disabled={submitting}
            aria-label={failure ? "Retry" : "Approve"}
          >
            {approveButtonLabel}
          </button>
          <button className="pgs-decline" onClick={handleDecline} disabled={submitting} aria-label="Decline">
            Decline
          </button>
        </div>
      ) : capState === "loading" ? (
        <span className="st-approval-card__pending">Preparing approval…</span>
      ) : capState === "error" ? (
        <button className="st-approval-card__retry" onClick={loadCapability}>Try again</button>
      ) : null}
    </div>
  )
}

// Fix #4 — the failed-send affordance, rendered ANCHORED inside its own user turn (not always-last)
// and auto-cleared on the durable echo. Copy is bucket-aware off classifySendError's kind:
//   awaiting (409, session blocked on an approval/ask card) → "Vesta is waiting on you…" + Send again
//   busy (409, not persisted, agent still replying) → "Vesta is still replying." + Send again
//   retryable (5xx / network, idempotent re-POST)   → "Couldn't send." + Retry
//   fatal (422/401/403, not persisted, same body fails again) → plain-language reason + NO button
// The reason line comes from sendFailureMessage (status + the server's error string).
// The button is the modern rounded .st-retry-btn (neutral secondary) — never a bare/gray control.
function SendNotice({ turn, onRetry }) {
  const kind = (turn && turn.error && turn.error.kind) || "retryable"
  const copy =
    kind === "awaiting"
      ? { msg: "Vesta is waiting on you — respond to the request above, then send.", label: "Send again" }
      : kind === "busy"
        ? { msg: "Vesta is still replying.", label: "Send again" }
        : {
            msg: sendFailureMessage(kind, turn && turn.error && turn.error.status, turn && turn.error && turn.error.serverError),
            label: kind === "fatal" ? null : "Retry",
          }
  return (
    <div className="st-send-notice" role="alert" data-testid="st-send-notice">
      <span className="st-send-notice__msg">{copy.msg}</span>
      {copy.label && (
        <button type="button" className="st-retry-btn" onClick={onRetry}>{copy.label}</button>
      )}
    </div>
  )
}

// S-A8: copy the RAW markdown source of a durable assistant turn to the clipboard, with an
// insecure-origin fallback (navigator.clipboard is undefined on non-HTTPS/non-localhost). Shows
// "Copied" for ~1.4s. copyableText(turn) returns what the model actually wrote, not rendered HTML.
function copyToClipboard(text) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text)
    }
  } catch (_e) { /* fall through to the legacy path */ }
  // Insecure-origin fallback: a hidden textarea + execCommand('copy').
  try {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "absolute"
    ta.style.left = "-9999px"
    document.body.appendChild(ta)
    ta.select()
    document.execCommand("copy")
    document.body.removeChild(ta)
  } catch (_e) { /* best-effort; copy simply no-ops on a locked-down host */ }
  return Promise.resolve()
}

function CopyButton({ turn }) {
  const [copied, setCopied] = React.useState(false)
  const onCopy = React.useCallback(() => {
    Promise.resolve(copyToClipboard(copyableText(turn))).finally(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    })
  }, [turn])
  return (
    <button type="button" className="st-copy" onClick={onCopy} aria-label="Copy reply">
      {/* both icons stay mounted; .st-copy__ic.on cross-fades them (opacity/scale/blur) so the
          swap has a real exit animation instead of a hard DOM toggle */}
      <span className={`st-copy__ic${copied ? "" : " on"}`} aria-hidden="true"><ClipboardSVG /></span>
      <span className={`st-copy__ic${copied ? " on" : ""}`} aria-hidden="true"><CheckSVG /></span>
    </button>
  )
}

// S-A8: clipboard icon for the copy button (rest state)
function ClipboardSVG() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// S-A8: checkmark icon for the copy button (copied state)
function CheckSVG() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// LOOP B / DEFECT (V-icon): the per-bubble "V" avatar (.st-avatar) was removed at all four
// agent-side render sites (directive, settled, thinking, live) per Billy's preference — a
// multi-bubble reply should read as one message, and the avatar could flash mid-stream. The
// VMarkAvatar component is intentionally gone; .st-avatar must not reappear in this file.

// C9: inline ArrowUp SVG (17px) replacing the ↑ glyph
function ArrowUpSVG() {
  return (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
      <path d="M8.5 13.5V3.5M8.5 3.5L4 8M8.5 3.5L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// C10: inline chevron-down SVG (12px) replacing the ▾ glyph
function ChevronDownSVG() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// Task 5: inline paperclip SVG — the composer's "Attach a file" plus button.
function PaperclipSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M13 7.2l-5.1 5.1a2.6 2.6 0 01-3.7-3.7l5.1-5.1a1.7 1.7 0 012.5 2.5L6.7 11.1a.85.85 0 01-1.2-1.2l4.6-4.6"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// S-H1: inline Stop (square) SVG — the Send button morphs to this while the agent is working.
function StopSquareSVG() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" fill="currentColor" />
    </svg>
  )
}

// C12: inline X SVG (12px) replacing × character
function XSmallSVG() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}

// ── m-r1-08: POST a human turn to the messages endpoint ──────────────────────
// §3.1 UUID contract: message_id is a v4 UUID from crypto.randomUUID().
// model is the short-id (e.g. "opus") — the server resolves to the full canonical id.
// UI-1: effort is included via buildSendBody when the model supports it.
async function postMessage({ companySlug, chatId, messageId, text, model, effort, attachments }) {
  const url = `/api/agent/companies/${companySlug}/chats/${chatId}/messages`
  const body = buildSendBody({ text, message_id: messageId, model, effort, attachments })
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
    },
    body: JSON.stringify(body),
  })
  if (!resp.ok) {
    // S-B6: surface the HTTP status on the error so the catch can classifySendError(status).
    // Decline-resume fix: also surface the server's JSON error string (best-effort) so a 409 can
    // distinguish "awaiting your input" (approval card pending) from "still replying".
    const err = new Error(`POST messages failed: ${resp.status}`)
    err.status = resp.status
    err.serverError = await readServerError(resp)
    throw err
  }
  return resp.json()
}

// Best-effort read of the backend's JSON `error` string off a failed response. Never throws —
// a non-JSON body (proxy error page) just yields null and the classifier falls back on status.
async function readServerError(resp) {
  try {
    const data = await resp.json()
    if (data && data.error) return data.error
    // A persist failure answers { errors: [...] } instead of { error }.
    if (data && Array.isArray(data.errors) && data.errors.length) return data.errors.join("; ")
    return null
  } catch (_e) {
    return null
  }
}

// ── m3-14 (was unwired): create a new human chat with the first turn as the prompt ──
// POST /api/agent/companies/:slug/chats { prompt, model, effort? } → Sessions::Start creates
// the Session AND starts the agent run; returns { id }. The caller sets this id as activeChat
// so SessionChannel subscribes and the streamed reply renders. model is the short-id; the
// server resolves it to the canonical full id (and allowlist-rejects off-list models).
// UI-1: effort sent when model supports it (buildSendBody handles the gate).
async function createChat({ companySlug, text, messageId, model, effort, attachments }) {
  const url = `/api/agent/companies/${companySlug}/chats`
  // S-B1: buildCreateChatBody maps to the createChat wire contract (`prompt` not `text`) AND
  // threads the client message_id so the durable echo dedups the optimistic first user bubble.
  // Task 5: attachments (already wire-shaped) flow through when non-empty.
  const body = buildCreateChatBody({ text, messageId, model, effort, attachments })
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
    },
    body: JSON.stringify(body),
  })
  if (!resp.ok) {
    // S-B6: surface the HTTP status on the error so the catch can classifySendError(status).
    const err = new Error(`POST chats failed: ${resp.status}`)
    err.status = resp.status
    err.serverError = await readServerError(resp)
    throw err
  }
  return resp.json()
}

// ── m-r1-08: useSessionChannel — subscribe to SessionChannel for streaming ───
// Canonical handshake (same as ObserveSessionPage.js and observeConsumer.js):
//   1. Subscribe; buffer live frames before replay.
//   2. Request backlog via replay(last_seq=0).
//   3. Flush buffered live frames AFTER replay through the SAME canonical reducer.
//   4. Gap resync: if needsResync, re-request from resyncFrom.
// Uses the CANONICAL observeReducer from pages/observeReducer.js (NOT the shell copy).
function useSessionChannel(chatId) {
  const [state, dispatch] = useReducer(observeReducer, undefined, initialObserveState)
  // S-D8: connection-lifecycle state driven through the pure connectionReducer from the channel
  // callbacks (disconnected → 'reconnecting', rejected → 'rejected', connected → cleared). A
  // mid-stream drop now surfaces a subtle chip instead of freezing silently.
  const [connection, connectionDispatch] = useReducer(connectionReducer, { connectionState: "connected" })
  const subRef = useRef(null)
  const maxSeqRef = useRef(0)
  const needsResyncRef = useRef(false)
  const resyncFromRef = useRef(0)
  maxSeqRef.current = state.maxSeq
  needsResyncRef.current = state.needsResync
  resyncFromRef.current = state.resyncFrom
  const liveBufferRef = useRef([])
  const handshakeDoneRef = useRef(false)

  // Gap resync: re-request backlog from resyncFrom when a gap is detected.
  useEffect(() => {
    if (state.needsResync && subRef.current) {
      subRef.current.perform("replay", { last_seq: state.resyncFrom })
    }
  }, [state.needsResync, state.resyncFrom])

  useEffect(() => {
    if (!chatId) return

    const flushLiveBuffer = () => {
      const buffered = liveBufferRef.current
      liveBufferRef.current = []
      buffered.forEach((frame) => dispatch(frame))
    }

    const subscription = consumer.subscriptions.create(
      { channel: "SessionChannel", id: chatId },
      {
        connected() {
          handshakeDoneRef.current = false
          liveBufferRef.current = []
          const lastSeq = needsResyncRef.current ? resyncFromRef.current : maxSeqRef.current
          // S-D8: replay(lastSeq) runs on EVERY (re)connect, so a connected() after a drop
          // re-mounts the mid-stream reply via replay_batch and the chip clears.
          subscription.perform("replay", { last_seq: lastSeq })
          handshakeDoneRef.current = true
          flushLiveBuffer()
          connectionDispatch("connected")
        },
        // S-D8: a mid-stream drop → 'reconnecting' chip (working is not left hung — the
        // connectionState is orthogonal to it). The next connected() runs the replay backfill above.
        disconnected() { connectionDispatch("disconnected") },
        // S-D8: a rejected subscription → 'rejected' chip ("Chat unavailable — refresh").
        rejected()     { connectionDispatch("rejected") },
        received(data) {
          if (data && data.kind === "replay_batch") {
            dispatch(data)
            return
          }
          if (!handshakeDoneRef.current) {
            liveBufferRef.current.push(data)
            return
          }
          dispatch(data)
        },
      }
    )
    subRef.current = subscription
    return () => {
      subscription.unsubscribe()
      subRef.current = null
      // D5: the useReducer state persists across chatId changes (the reducer is created once at
      // the hook level, not re-keyed to chatId), so without this the prior chat's durable+live
      // state would bleed into the next subscription's replay. Dispatch a chat-scoped reset on
      // teardown to wipe eventsBySeq + liveDeltas before the new chat's subscription replays.
      dispatch({ kind: "reset", scope: "chat" })
    }
  }, [chatId])

  return { state, durableEvents: durableEvents(state), connectionState: connection.connectionState }
}

export default function AgentStudioChat({ askRef = null, composerSeed = "", sendNonce = 0, onClearRef, companySlug, onRequestChatWidth }) {
  const storage = safeStorage()

  const [model, setModel] = useState(() => resolveModel(null, readPersistedModel(storage)))
  const [modelOpen, setModelOpen] = useState(false)
  // UI-1: effort selector state — persisted, defaulting to medium
  const [effort, setEffort] = useState(() =>
    resolveEffort(null, readPersistedEffort(storage), resolveModel(null, readPersistedModel(storage)))
  )
  const [effortOpen, setEffortOpen] = useState(false)
  const pickerGroupRef = useRef(null)

  const onPickModel = useCallback((id) => {
    setModel(id)
    persistModel(id, storage)
    setModelOpen(false)
    setEffortOpen(false)
    // UI-1: when model changes, clamp effort to what the new model supports
    setEffort((prev) => resolveEffort(prev, readPersistedEffort(storage), id))
  }, [storage])

  const onPickEffort = useCallback((e) => {
    setEffort(e); persistEffort(e, storage); setEffortOpen(false)
  }, [storage])

  const onToggleModel = useCallback(() => {
    setEffortOpen(false)
    setModelOpen((open) => !open)
  }, [])

  const onToggleEffort = useCallback(() => {
    setModelOpen(false)
    setEffortOpen((open) => !open)
  }, [])

  // The two pickers behave as one popover group: clicking elsewhere or
  // pressing Escape dismisses either menu, with listeners present only while
  // a menu is open.
  useEffect(() => {
    if (!modelOpen && !effortOpen) return undefined

    const closePickers = () => {
      setModelOpen(false)
      setEffortOpen(false)
    }
    const onDocumentMouseDown = (event) => {
      if (!pickerGroupRef.current || !pickerGroupRef.current.contains(event.target)) {
        closePickers()
      }
    }
    const onDocumentKeyDown = (event) => {
      if (event.key === "Escape") closePickers()
    }

    document.addEventListener("mousedown", onDocumentMouseDown)
    document.addEventListener("keydown", onDocumentKeyDown)
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown)
      document.removeEventListener("keydown", onDocumentKeyDown)
    }
  }, [modelOpen, effortOpen])

  // m3-nav-morph: activeChat is set by the nav's vesta:nav:select-chat event (not internal state).
  // The nav owns the chat list; this component listens for selection events.
  //
  // Item #7 taste (i), 2026-07-28: activeChat INITIALIZES from the sessionStorage mirror instead
  // of null. Two birds: (1) any agent-shell mount in the same tab (BROWSER back from the library,
  // a refresh, re-entering agent mode) reopens the chat the user had open — the same continuity
  // the in-app back button provides; (2) the old null initial state made the mount-time mirror
  // effect below WIPE the mirror before anything could read it (the "back loses the chat" half of
  // the finding). VsNavLower seeds its is-active highlight from the same mirror, so pane and nav
  // agree. An explicit New chat still clears the mirror (activeChat → null → removeItem).
  const [activeChat, setActiveChat] = useState(() => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    return readActiveChatId(store)
  })
  // Item #7: mirror activeChat.id into sessionStorage on EVERY change (covers the select-chat event
  // AND the lazy first-send chat creation, which sets activeChat directly without an event) so the
  // panel-side View-all click site (TriggeredTasksList — a different React subtree that cannot see
  // this state) can stage the return descriptor with the open chat's id. Null clears the mirror.
  useEffect(() => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    mirrorActiveChat(store, activeChat)
  }, [activeChat])
  // Chat privacy 3e — the active chat's META (title/visibility/sharedUserIds/creator), carried by the
  // nav's companion 'vesta:nav:chat-selected' event, so the slim header can render the visibility icon
  // and open the shared modal. Null on a new/pending chat (header hidden until a real chat is active).
  const [activeChatMeta, setActiveChatMeta] = useState(null)
  // Chat privacy 3e — open state for the header-icon modal (same ChatVisibilityModal component).
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false)

  useEffect(() => {
    function onSelectChat(e) {
      const id = e.detail && e.detail.id
      // freshRun (agent-job review seed): the chat was created out-of-band with a run already in
      // flight, so mirror sendText's optimistic arm — stamp justCreatedRef BEFORE setActiveChat so
      // the [activeChat] reset effect doesn't wipe the arm, then raise the thinking indicator
      // (sentAtSeq 0 = new chat, no durable events yet; the run's own terminal clears it).
      if (e.detail && e.detail.freshRun && id) {
        justCreatedRef.current = id
        setSentAtSeq(0)
        setSentSinceTerminal(true)
        setStopped(false)
        // E-thinking-verbs (template-form variant): arming sentSinceTerminal alone isn't enough —
        // the working-gated ThinkingIndicator further below is nested INSIDE the !convo.isEmpty
        // branch, and convo.isEmpty is driven by allMessages.length (mergeTimeline of userTurns/durableEvents/
        // liveTurns). This chat's first turn was POSTed directly by the job-review seed (never
        // pushed onto userTurns via sendText), so without this, allMessages stays [] — convo.isEmpty
        // stays true, and the WHOLE thread (seed bubble + indicator) hides behind the empty hero
        // until the durable echo replays over SessionChannel. Mirror sendText's OTHER optimistic arm
        // (setUserTurns) here: plant the seed text as a local user turn (anchorSeq 0, matching
        // sentAtSeq 0 — a brand-new chat) so convo.isEmpty flips false THIS tick. REPLACE (not
        // append) — this is a different chat than whatever was active before, so the prior chat's
        // local turns must not leak in (the [activeChat] reset effect below is skipped for this
        // adoption, same as the plain new-chat-creation path). seedMessageId matches the message_id
        // already POSTed to the chats API, so mergeTimeline's dedup drops this optimistic turn
        // cleanly once its durable echo lands (no duplicate bubble).
        if (e.detail.seedText) {
          setUserTurns([{ role: "user", text: e.detail.seedText, ref: null, messageId: e.detail.seedMessageId || null, anchorSeq: 0 }])
        }
      }
      setActiveChat(id)
    }
    // Chat privacy 3e — the meta channel (additive to the frozen id-only select-chat contract).
    function onChatSelected(e) { setActiveChatMeta(e && e.detail ? e.detail : null) }
    // Chat privacy 3e — reflect a visibility change made from any surface onto the header icon.
    function onVisibilityChanged(e) {
      const d = e && e.detail
      if (!d) return
      setActiveChatMeta((prev) => (prev && String(prev.id) === String(d.id) ? { ...prev, visibility: d.visibility } : prev))
    }
    // D3: "New chat" clears the pane to an empty new-chat view — NO POST /chats. Setting
    // activeChat to null drops back to the empty hero (convo.isEmpty) AND triggers the
    // [activeChat] reset effect below (wipes userTurns/sending/sentSinceTerminal/input); we also
    // clear the composer input here so the new-chat view starts blank. The real chat (and its
    // Session) is lazy-created on first send, not on this button.
    function onNewChat() {
      setActiveChat(null)
      setActiveChatMeta(null) // Chat privacy 3e — no header on a fresh/pending chat
      setUserTurns([])
      setInput("")
    }
    window.addEventListener("vesta:nav:select-chat", onSelectChat)
    window.addEventListener("vesta:nav:chat-selected", onChatSelected)
    window.addEventListener("vesta:chat:visibility-changed", onVisibilityChanged)
    window.addEventListener("vesta:nav:new-chat",    onNewChat)
    return () => {
      window.removeEventListener("vesta:nav:select-chat", onSelectChat)
      window.removeEventListener("vesta:nav:chat-selected", onChatSelected)
      window.removeEventListener("vesta:chat:visibility-changed", onVisibilityChanged)
      window.removeEventListener("vesta:nav:new-chat",    onNewChat)
    }
  }, [])

  const [input, setInput] = useState("")
  // m-r1-08: messages now merges local user turns + durable assistant turns from SessionChannel.
  const [userTurns, setUserTurns] = useState([]) // local user messages for immediate feedback
  const [sending, setSending] = useState(false)
  // S4-2: in-flight flag for the thinking indicator. SEPARATE from `sending` —
  // `sending` clears at HTTP 201 (before the agent starts); `sentSinceTerminal`
  // stays true until the run produces a token or terminates (durable stream signal).
  const [sentSinceTerminal, setSentSinceTerminal] = useState(false)
  // CHAT-8: the max durable event seq at the instant of the last send. isWorking gates its "done"
  // checks on seq > sentAtSeq so a STALE prior run's terminal (seq <= sentAtSeq, still in the
  // stream because run2's RUN_STARTED hasn't arrived) can't drop the spinner for a fresh send.
  const [sentAtSeq, setSentAtSeq] = useState(0)
  const taRef = useRef(null)
  // S-E5 (P0-2): synchronous same-tick send latch. Claimed at the TOP of doSend BEFORE any await,
  // so a second Enter in the same tick is dropped before it can mint a second UUID / fire a 2nd POST.
  const inFlightRef = useRef(false)
  // S-H1 (P0-4): client-side Stop. No backend cancel route exists, so Stop just abandons the live
  // lane locally — suppress the streaming render + indicator, re-enable the composer. The server run
  // still finishes (its durable turn lands normally); `stopped` is cleared on the next send / chat switch.
  const [stopped, setStopped] = useState(false)

  // Task 5: composer attachments (ChatGPT-style plus-button + drag-drop). Each entry is
  // {id, name, mimeType, size, url|null, status: 'uploading'|'done'|'error'}. attError is a
  // transient rejection line (auto-clears 4s); isDragover drives the dashed-border affordance.
  const [attachments, setAttachments] = useState([])
  const [attError, setAttError] = useState(null)
  const [isDragover, setIsDragover] = useState(false)
  const fileInputRef = useRef(null)
  const attErrorTimerRef = useRef(null)

  const flashAttError = useCallback((reason) => {
    setAttError(reason)
    if (attErrorTimerRef.current) clearTimeout(attErrorTimerRef.current)
    attErrorTimerRef.current = setTimeout(() => setAttError(null), 4000)
  }, [])

  // Decline-resume fix — the composer's send gate (canSend && !sending && !working) used to swallow
  // Enter/click SILENTLY while a turn was in flight: text stayed in the box, no POST, no feedback.
  // gateNotice is a transient line (same 4s auto-clear pattern as attError) that says WHY the send
  // didn't go, with approval-aware copy when the run is paused on a needs-you card.
  const [gateNotice, setGateNotice] = useState(null)
  const gateNoticeTimerRef = useRef(null)
  const flashGateNotice = useCallback((msg) => {
    setGateNotice(msg)
    if (gateNoticeTimerRef.current) clearTimeout(gateNoticeTimerRef.current)
    gateNoticeTimerRef.current = setTimeout(() => setGateNotice(null), 4000)
  }, [])

  const removeAttachment = useCallback((id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  // Upload one validated file to Uploadcare (the `uploadcare` JS global is already loaded on the
  // agent page via the shared layout's uploadcare_include_tag). On resolve stamp {url, status:'done'};
  // on reject stamp {status:'error'}. Bytes never touch Rails — only the resulting cdnUrl does.
  const uploadAttachment = useCallback((id, file) => {
    const uc = typeof window !== "undefined" ? window.uploadcare : undefined
    if (!uc || typeof uc.fileFrom !== "function") {
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error" } : a)))
      return
    }
    uc.fileFrom("object", file).done((info) => {
      const url = (info && (info.cdnUrl || info.originalUrl)) || null
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, url, status: url ? "done" : "error" } : a)))
    }).fail(() => {
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error" } : a)))
    })
  }, [])

  // Validate + enqueue a FileList (from the file input or a drop). Rejections render a transient
  // .st-att-error line; accepted files become 'uploading' chips and kick off their Uploadcare upload.
  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    setAttachments((prev) => {
      let next = prev
      for (const file of files) {
        const check = validateAttachmentFile(file, next.length)
        if (!check.ok) {
          flashAttError(check.reason)
          continue
        }
        const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `att-${Date.now()}-${next.length}`
        next = [...next, { id, name: file.name, mimeType: file.type, size: file.size, url: null, status: "uploading" }]
        // Kick off the upload after state commits (the chip is already in `next`).
        Promise.resolve().then(() => uploadAttachment(id, file))
      }
      return next
    })
  }, [flashAttError, uploadAttachment])

  const onFileInputChange = useCallback((e) => {
    addFiles(e.target.files)
    e.target.value = "" // allow re-picking the same file
  }, [addFiles])

  const onComposerDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragover(true)
  }, [])
  const onComposerDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDragover(false)
  }, [])
  const onComposerDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragover(false)
    if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files)
  }, [addFiles])

  const uploading = hasUploadingAttachment(attachments)

  // m-r1-08: subscribe to SessionChannel for the active chat's durable event stream.
  // S-D8: connectionState drives a subtle connection chip (reconnecting/rejected).
  const { state: streamState, durableEvents: streamEvents, connectionState } = useSessionChannel(activeChat)

  // CHAT-6: the max durable seq present RIGHT NOW. Stamped onto each optimistic user turn at
  // send time (as anchorSeq) so mergeTimeline anchors the pending turn AFTER the durable turns
  // that existed when it was submitted — and BEFORE any later (faster) assistant reply. Kept in a
  // ref so the send callbacks read the live value at the send instant without re-creating.
  const maxDurableSeqRef = useRef(0)
  useEffect(() => {
    let m = 0
    for (const e of streamEvents) if (e.seq > m) m = e.seq
    maxDurableSeqRef.current = m
  }, [streamEvents])

  // D2(b): freeze each live bubble's chronological anchor (declared below `liveDeltas`).
  const liveAnchorRef = useRef({})

  // When doSend creates a chat from a first message, it stamps the new id here so the
  // chat-change reset below preserves the optimistic user turn (instead of wiping it).
  const justCreatedRef = useRef(null)

  // Reset user turns when the chat changes (switching chat or new chat) — EXCEPT when the
  // change is from a chat we just created out of the first message (keep that turn visible).
  useEffect(() => {
    if (justCreatedRef.current && justCreatedRef.current === activeChat) {
      justCreatedRef.current = null
      return
    }
    setUserTurns([])
    setSending(false)
    setSentSinceTerminal(false)
    setSentAtSeq(0)
    setStopped(false) // S-H1: a chat switch clears any client-side Stop from the prior chat.
  }, [activeChat])

  // Live typewriter deltas (ephemeral — not yet reconciled to durable).
  const liveDeltas = streamState.liveDeltas

  // D2(b): freeze each live bubble's chronological anchor = the max durable seq present when its
  // messageId FIRST appeared (the SAME anchor optimistic user turns use). Kept stable across later
  // durable growth so a still-streaming bubble doesn't drift; pruned when its lane reconciles away.
  // Unrecorded ids fall back to the current max durable seq (≈ first appearance) in mergeTimeline.
  useEffect(() => {
    const anchors = liveAnchorRef.current
    for (const mid of Object.keys(liveDeltas)) {
      if (anchors[mid] == null) anchors[mid] = maxDurableSeqRef.current
    }
    for (const mid of Object.keys(anchors)) {
      if (!(mid in liveDeltas)) delete anchors[mid]
    }
  }, [liveDeltas])

  // S4-2 / CHAT-8: working = "agent is thinking" — armed on local send, cleared only by a
  // token/terminal STRICTLY AFTER the send (seq > sentAtSeq). NEVER derived from `sending`.
  const workingRaw = useMemo(
    () => isWorking({ events: streamEvents, liveDeltas, sentSinceTerminal, sentAtSeq }),
    [streamEvents, liveDeltas, sentSinceTerminal, sentAtSeq]
  )
  // S-H1: a client-side Stop forces working off (re-enables the composer) even while the server
  // run is still in flight. The next send clears `stopped`, so this never sticks across turns.
  const working = workingRaw && !stopped

  // Item A — the run is PAUSED waiting on the human (an unresolved approval/ask_user card, no
  // terminal). Same durable signal + sentAtSeq baseline isWorking uses. When true we swap the
  // cycling "thinking" indicator for a calm static "your turn" affordance (the composer stays
  // gated via `working` — isWorking is untouched and stays true through the pause).
  const awaiting = useMemo(
    () => awaitingApproval({ events: streamEvents, sentAtSeq }),
    [streamEvents, sentAtSeq]
  )
  const awaitingNeedsCapability = useMemo(
    () => awaitingApprovalRequiresCapability({ events: streamEvents, sentAtSeq }),
    [streamEvents, sentAtSeq]
  )

  // S4-2 / CHAT-8: reset sentSinceTerminal only when THIS send's run terminates (seq > sentAtSeq).
  // Gating on sentAtSeq (not the latest *started* run) stops a stale prior-run terminal from
  // flipping the flag off in the window between a 2nd+ send and run2's server RUN_STARTED.
  useEffect(() => {
    if (sentSinceTerminal && runTerminated(streamEvents, sentAtSeq)) {
      setSentSinceTerminal(false)
    }
  }, [streamEvents, sentSinceTerminal, sentAtSeq])

  // S4-1: seq-ordered monotonic timeline. mergeTimeline:
  //   - coalesces TEXT_MESSAGE_CONTENT by messageId (assistant turns, seq-keyed by firstSeq)
  //   - emits a user turn for every durable TEXT_MESSAGE role:user (seq-keyed)
  //   - drops any optimistic userTurns entry whose messageId matches a durable echo (UUID-keyed dedup)
  //   - ANCHORS surviving optimistic turns by submitIndex (CHAT-1: fixes the new-chat race where a
  //     newer user message would otherwise render below the fast assistant reply)
  // Replaces the old [...userTurns, ...assistantTurns] concat which rendered all user turns first.
  // CHAT-1 seam: userTurns are appended (in both doSend and the m3-15 handoff) as
  // { role, text, ref, messageId } WITHOUT submitIndex. toOptimisticUserTurns stamps
  // submitIndex = array position (already submit order) so mergeTimeline's anchoring actually
  // fires in the browser — without it the live ordering bug is not really fixed.
  // D2(b): fold the live ephemeral lane INTO this one ordered timeline (was a separate, seq-less,
  // always-last concat in the JSX → out-of-order settling). Each live bubble anchors by the max
  // durable seq at its first appearance. Suppressed after a client-side Stop (mirrors the old gate).
  const allMessages = useMemo(
    () => mergeTimeline({
      durableEvents: streamEvents,
      optimisticUserTurns: toOptimisticUserTurns(userTurns),
      liveTurns: stopped ? [] : liveTurnsFromDeltas(liveDeltas, liveAnchorRef.current, maxDurableSeqRef.current),
    }),
    [streamEvents, userTurns, liveDeltas, stopped]
  )

  // m-r1-08: the real send — POST to messages API, then the streaming channel delivers the reply.
  // H-UI: parameterized by text (sendText) so a suggestion pill can send its full prompt without
  // depending on stale composer `input` (setInput is async — a pill doing setInput+doSend would send
  // the empty closure value). doSend just forwards the composer input; everything else is unchanged.
  const sendText = useCallback(async (rawText) => {
    const text = (rawText || "").trim()
    // S-E5 (P0-1): gate on the TEXT ARG (canSend) + run lifecycle — never send while the agent is
    // still replying. This is exactly canSubmit's contract (canSend && !sending && !working) applied
    // to the argument instead of the composer input (H-UI). A working-gated attempt now flashes a
    // transient explanation instead of a silent no-op (the text stays in the composer for later).
    if (canSend(text) && !sending && working) {
      flashGateNotice(awaiting
        ? "Vesta is waiting on you — approve or decline the request above first."
        : "Vesta is still replying — you can send when this turn finishes.")
      return
    }
    if (!canSend(text) || sending || working) return
    // Task 5: never send while an attachment is still uploading — its url isn't ready yet.
    if (hasUploadingAttachment(attachments)) return
    // S-E5 (P0-2): synchronous same-tick latch BEFORE any await. acquireSendLock returns false if a
    // send is already in flight, dropping a same-tick double-Enter before it mints a 2nd UUID/POST.
    if (!acquireSendLock(inFlightRef)) return
    try {
      // S-H1: a fresh send clears any prior client-side Stop so the new run's live lane renders.
      setStopped(false)
      const messageId = crypto.randomUUID()
      // Task 5: the done-status attachments mapped to the wire shape ({url, mime_type, name, size}).
      const wireAttachments = attachmentsToWire(attachments)

      // Immediately show the user turn in the thread (optimistic).
      // messageId links this optimistic turn to its durable echo for dedup (S4-1).
      // CHAT-6: anchorSeq = max durable seq present at send time → mergeTimeline anchors this
      // pending turn AFTER the prior durable turns and BEFORE any later assistant reply.
      // Task 5: the sent user bubble's thumbnails come from the DURABLE echo payload.attachments
      // (chatTimeline maps them on) — the composer chips stay visible until the send succeeds, so we
      // deliberately do NOT stamp attachments on the optimistic turn (no double-display of the same
      // files in the composer AND the bubble mid-send).
      setUserTurns((prev) => [...prev, { role: "user", text, ref: askRef || null, messageId, anchorSeq: maxDurableSeqRef.current }])
      // S4-2: arm the thinking indicator immediately (zero-latency, before RUN_STARTED broadcasts).
      // CHAT-8: snapshot the send-time baseline seq so only a NEWER run's terminal can clear it.
      setSentAtSeq(maxDurableSeqRef.current)
      setSentSinceTerminal(true)
      setInput("")
      if (typeof onClearRef === "function") onClearRef()

      if (!companySlug) return

      setSending(true)
      try {
        if (activeChat) {
          // Existing chat → post a follow-up turn to the messages endpoint.
          await postMessage({ companySlug, chatId: activeChat, messageId, text, model, effort, attachments: wireAttachments })
          // DEFECT #4: a SEND (not a select) bumps this chat to the top of the nav. Mirror the
          // chat-created dispatch shape; the nav optimistically sets lastActive=now so the chat
          // floats to the top by recency immediately (no refetch needed).
          window.dispatchEvent(new CustomEvent("vesta:nav:chat-activity", { detail: { id: String(activeChat) } }))
        } else {
          // m3-14: brand-new conversation → create the chat with this first turn as the prompt.
          // Sessions::Start (server) creates the Session AND starts the run; we adopt the new id
          // so SessionChannel subscribes and the streamed reply renders. justCreatedRef keeps the
          // optimistic user turn from being wiped by the chat-change reset.
          const created = await createChat({ companySlug, text, messageId, model, effort, attachments: wireAttachments })
          justCreatedRef.current = created.id
          // D2: tell the nav a chat now exists so it shows a real titled, highlighted entry
          // (no page refresh). chatCreatedEvent normalizes the id to a String; the title seed
          // is the first message text (the nav truncates/refines it on its own refetch).
          // D2: tell the nav a chat now exists so it shows a real titled, highlighted entry
          // (no page refresh). chatCreatedEvent normalizes the id to a String; the title seed
          // is the first message text (the nav truncates/refines it on its own refetch).
          window.dispatchEvent(new CustomEvent("vesta:nav:chat-created", { detail: chatCreatedEvent(created.id, text) }))
          setActiveChat(created.id)
        }
        // Task 5: chips are cleared ONLY on a successful send (they SURVIVE a failed send so the
        // user can retry) — this line runs only when the await above did not throw.
        setAttachments([])
        setAttError(null)
      } catch (err) {
        // S-B6 (P0-3): a failed send no longer vanishes silently. Stamp the optimistic turn
        // {status:'failed', error} so it renders a "Couldn't send. Retry" affordance, and clear the
        // indicator (sentSinceTerminal) so the spinner stops hanging. classifySendError(status) buckets
        // the failure (busy/fatal/retryable). NOTE: we do NOT touch the same-tick setSentSinceTerminal(true)
        // arm above — that's the CHAT-8 flicker fix; only THIS error path clears it.
        const status = err && err.status
        markTurnFailed(messageId, { status, kind: classifySendError(status, err && err.serverError), message: err && err.message, serverError: err && err.serverError })
        setSentSinceTerminal(false)
      } finally {
        setSending(false)
      }
    } finally {
      // S-E5: always release the same-tick latch (success, early-return, or throw).
      releaseSendLock(inFlightRef)
    }
  }, [sending, working, awaiting, flashGateNotice, askRef, activeChat, companySlug, model, effort, onClearRef, attachments])

  // doSend keeps its exact old behavior — it forwards the composer input (canSend(input) here ≡
  // the old canSubmit({input, sending, working}) gate, byte-for-byte the Send button's disabled logic).
  const doSend = useCallback(() => sendText(input), [sendText, input])

  // S-H1 (P0-4): client-side Stop. Abandons the live lane locally — flips `stopped` (which forces
  // `working` off and suppresses the streaming render below) so the composer re-enables immediately.
  // The server run still completes and its durable turn lands; we just stop animating it.
  const doStop = useCallback(() => {
    setStopped(true)
    setSentSinceTerminal(false)
  }, [])

  // S-B6: stamp a failed status onto the optimistic user turn identified by messageId.
  const markTurnFailed = useCallback((messageId, error) => {
    setUserTurns((prev) => prev.map((t) => (t.messageId === messageId ? { ...t, status: "failed", error } : t)))
  }, [])

  // S-B6: re-POST a failed turn using the SAME messageId (idempotent — the backend dedups on
  // event_id, so a retry never duplicates the turn). Clears the failed status + re-arms the indicator.
  const retrySend = useCallback(async (turn) => {
    if (!turn || !companySlug) return
    const { messageId, text } = turn
    setUserTurns((prev) => prev.map((t) => (t.messageId === messageId ? { ...t, status: undefined, error: undefined } : t)))
    setStopped(false)
    setSentAtSeq(maxDurableSeqRef.current)
    setSentSinceTerminal(true)
    setSending(true)
    // Task 5: re-send the surviving composer chips (they persisted through the failed send).
    const wireAttachments = attachmentsToWire(attachments)
    try {
      if (activeChat) {
        await postMessage({ companySlug, chatId: activeChat, messageId, text, model, effort, attachments: wireAttachments })
      } else {
        const created = await createChat({ companySlug, text, messageId, model, effort, attachments: wireAttachments })
        justCreatedRef.current = created.id
        // D2: same nav notification on the retry-create path.
        // D2: same nav notification on the retry-create path.
        window.dispatchEvent(new CustomEvent("vesta:nav:chat-created", { detail: chatCreatedEvent(created.id, text) }))
        setActiveChat(created.id)
      }
      // Task 5: retry succeeded → clear the chips (parity with the primary send path).
      setAttachments([])
      setAttError(null)
    } catch (err) {
      const status = err && err.status
      markTurnFailed(messageId, { status, kind: classifySendError(status, err && err.serverError), message: err && err.message, serverError: err && err.serverError })
      setSentSinceTerminal(false)
    } finally {
      setSending(false)
    }
  }, [companySlug, activeChat, model, effort, markTurnFailed, attachments])

  // Task 11 — "Run again" handoff from the Artifact viewer: the viewer stashed a pre-filled prompt in
  // sessionStorage['vesta-artifact-rerun'] and navigated here (/activity?mode=agent). On mount, read +
  // CLEAR the key (so a back/forward never re-fires it) and auto-send the prompt through the ONE real
  // send path (sendText). Fires at most once per mount; sendText itself no-ops until companySlug is set.
  const artifactRerunFiredRef = useRef(false)
  useEffect(() => {
    if (artifactRerunFiredRef.current || !companySlug) return
    let stashed = null
    try {
      stashed = sessionStorage.getItem("vesta-artifact-rerun")
      if (stashed) sessionStorage.removeItem("vesta-artifact-rerun")
    } catch (_e) {
      return
    }
    if (!stashed) return
    artifactRerunFiredRef.current = true
    let prompt = ""
    try {
      prompt = (JSON.parse(stashed) || {}).prompt || ""
    } catch (_e) {
      prompt = ""
    }
    if (prompt) sendText(prompt)
  }, [companySlug, sendText])

  // m3-15: a board→chat handoff (the drawer's "Ask Vesta" send) auto-sends the question as a
  // thread turn with the task attached. Keyed on a monotonic nonce so each handoff fires once.
  const sentNonce = useRef(0)
  useEffect(() => {
    if (sendNonce && sendNonce !== sentNonce.current) {
      sentNonce.current = sendNonce
      // Seed the composer and auto-send (the handoff fires a real turn).
      if (composerSeed && canSend(composerSeed)) {
        const messageId = crypto.randomUUID()
        const text = composerSeed.trim()
        // CHAT-6: stamp anchorSeq (max durable seq at send time) so the pending turn anchors correctly.
        setUserTurns((prev) => [...prev, { role: "user", text, ref: askRef || null, messageId, anchorSeq: maxDurableSeqRef.current }])
        // S4-2: arm the thinking indicator for the handoff auto-send (same zero-latency contract).
        // CHAT-8: snapshot the send-time baseline seq (same as doSend).
        setSentAtSeq(maxDurableSeqRef.current)
        setSentSinceTerminal(true)
        if (typeof onClearRef === "function") onClearRef()
        // CHAT-7: mirror doSend's branch — when there's NO active chat, CREATE one (adopting the new
        // id via justCreatedRef so the optimistic turn survives the chat-change reset). The old code
        // gated on `if (activeChat && companySlug)` and silently dropped the send → forever-spinner.
        const decision = decideSend({ activeChat, companySlug })
        if (decision.action === "post") {
          postMessage({ companySlug: decision.companySlug, chatId: decision.chatId, messageId, text, model, effort }).catch(() => {})
        } else if (decision.action === "create") {
          createChat({ companySlug: decision.companySlug, text, messageId, model, effort })
            .then((created) => {
              justCreatedRef.current = created.id
              // D2: mirror doSend — notify the nav of the newly-created chat from the handoff.
              // D2: mirror doSend — notify the nav of the newly-created chat from the handoff.
              window.dispatchEvent(new CustomEvent("vesta:nav:chat-created", { detail: chatCreatedEvent(created.id, text) }))
              setActiveChat(created.id)
            })
            .catch(() => {})
        }
      }
      setInput("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendNonce])

  const convo = useMemo(() => buildConversation(allMessages), [allMessages])

  // Area B: the Suggestion Dock's combined task/job list (static per session) + its CTA/card
  // click handler — forwards to sendText, the ONE real send path (never setInput+doSend).
  const dockItems = useMemo(buildDockSuggestions, [])
  const onPickSuggest = useCallback((it) => sendText(it.msg), [sendText])
  // Area 5: the dock's collapsed→open header click asks the shell to auto-widen the chat
  // column. onRequestChatWidth is optional (Standard mode / any caller that doesn't mount the
  // shell's resizable panel) — SuggestionDock's onExpand?.() call stays a safe no-op there.
  const onDockExpand = useCallback(
    () => { onRequestChatWidth?.(SUGGESTIONS_TWO_COLUMN_MIN_WIDTH) },
    [onRequestChatWidth],
  )
  // Task 9 D-T9-h / item #7: the dock's "View all" entry point into the full library (slug-scoped
  // when we have a slug, flat otherwise). This dock renders ONLY inside the agent layout, so the
  // href always carries ?mode=agent — the library keeps the agent side nav instead of kicking the
  // user out to Standard chrome.
  const libraryHref = agentLibraryHref(companySlug)
  // Item #7: at click time stage the RETURN descriptor for the library back button — the open chat
  // (this component's own state; null on the empty hero = Board-origin-equivalent, no chat restore)
  // plus the persisted right-panel view (the shell persists activityView on every change, so the
  // stored value IS the current shell state). Navigation proceeds via the anchor's own href.
  const onLibraryNav = useCallback(() => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    stageReturn(store, {
      chatId: activeChat == null ? null : String(activeChat),
      activityView: readPersistedActivityView(safeStorage()) || "board",
    })
  }, [activeChat])

  // Fix #4 — the failed-send affordance is now carried THROUGH mergeTimeline on the optimistic turn
  // (status/error), so it renders anchored to its own bubble and auto-clears on the durable echo. The
  // old always-last `failedTurns` memo (which floated to the bottom + never cleared) is removed.

  // ── S-B4: auto-scroll (stick to bottom on growth, never yank a scrolled-up reader) ──
  // threadRef points at the .st-thread scroll container; prevLenRef remembers the prior
  // streamLen so shouldStickToBottom can tell growth from shrink/switch; userScrolledUpRef
  // tracks whether the reader has scrolled away from the bottom (set by onScroll below).
  const threadRef = useRef(null)
  const prevLenRef = useRef(0)
  const userScrolledUpRef = useRef(false)

  const onThreadScroll = useCallback(() => {
    const el = threadRef.current
    if (!el) return
    userScrolledUpRef.current = !isNearBottom(el)
  }, [])

  // streamLen GROWS as content arrives — turns + the live delta CHAR length (so a single long
  // streaming reply growing below the fold still re-sticks, P1-2) + 1 while the indicator shows.
  const streamLen =
    convo.turns.length +
    Object.values(liveDeltas).reduce((n, t) => n + (typeof t === "string" ? t.length : 0), 0) +
    (working ? 1 : 0)

  React.useLayoutEffect(() => {
    const el = threadRef.current
    if (!el) return undefined
    const prev = prevLenRef.current
    prevLenRef.current = streamLen
    if (shouldStickToBottom(prev, streamLen, userScrolledUpRef.current)) {
      el.scrollTop = el.scrollHeight
    }
    // ResizeObserver re-sticks when late content (images/tables) grows the thread while parked.
    if (typeof ResizeObserver === "undefined") return undefined
    const ro = new ResizeObserver(() => {
      const node = threadRef.current
      if (!node) return
      if (!userScrolledUpRef.current) node.scrollTop = node.scrollHeight
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [streamLen])

  const activeModel = modelById(model)
  // UI-1: effort options for the current model; empty array means picker is hidden (e.g. Haiku)
  const effortOptions = availableEfforts(model)

  const onComposerKey = (e) => {
    // S-E5: while the agent is replying (working), Enter is inert — no send, no UUID minted.
    if (working) {
      if (e.key === "Enter" && !e.shiftKey) e.preventDefault()
      return
    }
    const action = composerKeyAction(e, input)
    if (action === "send") { e.preventDefault(); doSend() }
    // 'newline' falls through to default textarea behavior; 'noop' does nothing
  }

  const onInput = (e) => {
    setInput(e.target.value)
    const el = taRef.current
    if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 150) + "px" }
  }

  // Composer element — extracted so it can be placed inside .st-hero (empty) or .st-composer-wrap (non-empty).
  // ONE textarea instance rendered per mount; the composerSeed effect targets whichever branch is active.
  const composerEl = (
    <div
      className={`st-composer ${askRef ? "is-referencing" : ""}${isDragover ? " is-dragover" : ""}`}
      onDragOver={onComposerDragOver}
      onDragLeave={onComposerDragLeave}
      onDrop={onComposerDrop}
    >
      {askRef && (
        <div className="st-ref">
          <span className="st-ref__dot" style={{ background: askRef.color || "#5349E5" }} />
          <span className="st-ref__name">{askRef.name}</span>
          <span className="st-ref__id">{askRef.id}</span>
          {/* C12: X SVG replaces × character */}
          <button type="button" className="st-ref__x" onClick={onClearRef} aria-label="Clear reference"><XSmallSVG /></button>
        </div>
      )}
      {/* Task 5: attachment chips (visual sibling of st-ref) — thumbnail for images, doc glyph for
          PDFs, filename, remove-X. A rejected file flashes a transient .st-att-error line. */}
      {attachments.length > 0 && (
        <div className="st-att-row">
          {attachments.map((a) => (
            <div key={a.id} className={`st-att st-att--${a.status}`}>
              {isImageAttachment(a.mimeType) && a.url
                ? <img className="st-att__thumb" src={`${a.url}-/resize/64x64/`} alt="" />
                : <span className="st-att__glyph" aria-hidden="true">{a.status === "uploading" ? "…" : "PDF"}</span>}
              <span className="st-att__name" title={a.name}>{a.name}</span>
              {a.status === "uploading" && <span className="st-att__status">Uploading…</span>}
              {a.status === "error" && <span className="st-att__status st-att__status--error">Failed</span>}
              <button type="button" className="st-att__x" onClick={() => removeAttachment(a.id)} aria-label={`Remove ${a.name}`}><XSmallSVG /></button>
            </div>
          ))}
        </div>
      )}
      {attError && <div className="st-att-error" role="alert">{attError}</div>}
      {/* Decline-resume fix: the send-gate explanation (transient, 4s). Reuses the .st-att-error
          transient-line styling — an existing styled element, not a new bare control. */}
      {gateNotice && <div className="st-att-error" role="status">{gateNotice}</div>}
      {/* Task 5: the hidden file input the plus button + drop target feed. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="st-attach__input"
        style={{ display: "none" }}
        onChange={onFileInputChange}
      />
      <textarea
        ref={taRef}
        rows={1}
        className="st-ta"
        // C3: placeholder "Do anything" (no ellipsis); "Ask about this task…" with ref
        placeholder={askRef ? "Ask about this task…" : "Do anything"}
        value={input}
        onChange={onInput}
        onKeyDown={onComposerKey}
      />
      <div className="st-composer-row">
        <div className="st-composer-tools">
          {/* Task 5: opens the file picker. Styled real button per house rules (never bare);
              paperclip glyph; aria-labelled. */}
          <button
            type="button"
            className="st-attach__btn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            aria-label="Attach a file"
            title="Attach a file"
          ><PaperclipSVG /></button>
          {/* model picker — VISIBLE (m3-13, CD-3 deviation-keeper); real routing is m-r1-08 */}
          <div className="st-picker-group" ref={pickerGroupRef}>
            <div className="st-mdl">
              <button
                type="button"
                className={`st-mdl__btn${modelOpen ? " is-open" : ""}`}
                onClick={onToggleModel}
                aria-haspopup="listbox"
                aria-expanded={modelOpen}
              >
                {activeModel.label}
                {/* C10: chevron SVG replaces ▾ */}
                <ChevronDownSVG />
              </button>
              {modelOpen && (
                <ul className="st-mdl__menu" role="listbox">
                  {MODELS.map((m) => (
                    <li
                      key={m.id}
                      role="option"
                      aria-selected={m.id === model}
                      className={`st-mdl__opt ${m.id === model ? "is-active" : ""}`}
                      onClick={() => onPickModel(m.id)}
                    >
                      <span className="st-mdl__label">{m.label}</span>
                      <span className="st-mdl__blurb">{m.blurb}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* UI-1: effort picker — hidden for Haiku (effortOptions is empty) */}
            {effortOptions.length > 0 && (
              <div className="st-eff">
                <button
                  type="button"
                  className={`st-eff__btn${effortOpen ? " is-open" : ""}`}
                  onClick={onToggleEffort}
                  aria-haspopup="listbox"
                  aria-expanded={effortOpen}
                  aria-label="Effort level"
                >
                  {effort}
                  <ChevronDownSVG />
                </button>
                {effortOpen && (
                  <ul className="st-eff__menu" role="listbox">
                    {effortOptions.map((e) => (
                      <li
                        key={e}
                        role="option"
                        aria-selected={e === effort}
                        className={`st-eff__opt${e === effort ? " is-active" : ""}`}
                        onClick={() => onPickEffort(e)}
                      >
                        {e}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        {/* C9 / S-H1: the Send button morphs to a Stop (square) while the agent is working.
            sendButtonMode({working}) picks the mode; Stop is enabled + aborts client-side (doStop). */}
        {sendButtonMode({ working }) === "stop" ? (
          <button
            type="button"
            className="st-send st-send--stop"
            onClick={doStop}
            aria-label="Stop"
          ><StopSquareSVG /></button>
        ) : (
          <button
            type="button"
            className="st-send"
            // Task 5: also disabled while any attachment is still uploading (its url isn't ready).
            disabled={!canSend(input) || sending || working || uploading}
            onClick={doSend}
            aria-label="Send"
            title={uploading ? "Uploading…" : undefined}
          ><ArrowUpSVG /></button>
        )}
      </div>
    </div>
  )

  // m3-nav-morph: .st-side is deleted. The chat nav (New chat / Search / Chats) lives in
  // VsNavLower.jsx inside nav.sidebar. This component owns only the conversation + composer.
  // §7: listen for 'vesta:nav:select-chat' to set activeChat (m-r1-08: real chat loading).
  const headerVisibility = (activeChatMeta && activeChatMeta.visibility) || "company"
  const showChatHeader = !!activeChat && !!activeChatMeta

  return (
    <div className="st-chat">
      {/* Chat privacy 3e — the slim chat header (built here; none existed before). Rendered only for a
          real (non-pending) active chat. Left: title. Right: a visibility icon button (lock=private /
          people=shared / building=company) that opens the SAME ChatVisibilityModal in 'chat' mode. */}
      {showChatHeader && (
        <div className="st-chat__header">
          <span className="st-chat__header-title">{activeChatMeta.title || "Chat"}</span>
          <button
            type="button"
            className="st-chat__vis-btn"
            data-testid="chat-visibility-icon"
            title="Sharing & visibility"
            aria-label={`Visibility: ${headerVisibility}. Change sharing & visibility`}
            onClick={() => setVisibilityModalOpen(true)}
          >
            <HeaderVisibilityIcon name={visibilityGlyph(headerVisibility)} />
          </button>
        </div>
      )}
      {/* conversation + composer — the nav owns the chat rail */}
      <main className="st-mid" aria-label="Conversation">
        {/* S-D8: subtle connection chip — surfaces a mid-stream ActionCable drop instead of
            freezing silently. 'reconnecting' (auto-recovering via replay backfill) vs 'rejected'
            (terminal — needs a refresh). Hidden while 'connected'. */}
        {connectionState === "reconnecting" && (
          <div className="st-conn st-conn--reconnecting" role="status" aria-live="polite">Reconnecting…</div>
        )}
        {connectionState === "rejected" && (
          <div className="st-conn st-conn--rejected" role="alert">Chat unavailable — refresh</div>
        )}
        {convo.isEmpty ? (
          // Area B: empty state — hero vertically centers above a bottom-pinned composer column
          // (dock + composer), so the composer no longer floats mid-screen. See area-B.md §2.
          <>
            <div className="st-hero">
              <div className="st-hero-copy">
                <h1 className="st-hero__h">{convo.heroTitle}</h1>
                <div className="st-hero__capabilities">
                  <p className="st-hero__lead">{convo.heroLead}</p>
                  <ul className="st-hero__list" role="list">
                    {convo.heroPoints.map((point) => <li key={point}>{point}</li>)}
                  </ul>
                </div>
              </div>
            </div>
            <div className="st-composer-wrap st-composer-wrap--tight">
              <div className="st-composer-col">
                {/* Open on a fresh page load. SuggestionDock's page-lifetime used flag keeps a
                    picked suggestion collapsed across the empty→conversation remount. */}
                <SuggestionDock defaultOpen={true} items={dockItems} onPick={onPickSuggest} onExpand={onDockExpand} libraryHref={libraryHref} onLibraryNav={onLibraryNav} />
                {composerEl}
              </div>
            </div>
          </>
        ) : (
          // C7: non-empty — thread + composer below in .st-composer-wrap
          <>
            <div className="st-thread" ref={threadRef} onScroll={onThreadScroll}>
              {convo.turns.map((t, i) => {
                // CHAT-6: stable key — messageId (durable/optimistic UUID) or seq, falling back to
                // index only as a last resort. Index-only keys caused reconcile flicker as turns
                // settled and re-anchored; a stable identity keeps React from remounting them.
                const key = t.messageId || t.seq || i
                // G5 (#22): a CUSTOM render directive lands as a { role:'directive' } turn — surface
                // it in-thread. A 'report' directive renders the in-thread ReportArtifact card
                // (title/summary/markdown body via the A5 react-markdown module). P4: an 'interaction'
                // directive renders ChatInteractionCard — an APPROVAL card that is ACTIONABLE for the
                // bound viewer (per-viewer capability minted on the live observe stream) and read-only
                // otherwise. This is the chat half of "approve from EITHER surface" (board = SessionDrawer).
                if (t.role === "directive") {
                  return (
                    <div key={key} className="st-turn st-turn--directive">
                      {t.kind === "report"
                        ? <ReportArtifact render={t.render} />
                        : <ChatInteractionCard turn={t} sessionId={activeChat} />}
                    </div>
                  )
                }
                // D2(b): a live (ephemeral, not-yet-durable) bubble — folded into this one ordered
                // lane by mergeTimeline so it sits in its chronological slot (no always-last leap).
                // Renders the streaming lane (no Copy button); settles in place when its durable body
                // lands and the reducer clears the live delta.
                if (t.live) {
                  return (
                    <div key={`live-${t.messageId}`} className="st-turn">
                      <div className="st-msg st-msg--live"><StreamingMarkdown text={t.text} /></div>
                    </div>
                  )
                }
                return (
                  <div key={key} className={`st-turn ${t.role === "user" ? "st-turn--user" : ""}`}>
                    {/* C19: user turns use .st-ubub; assistant turns keep .st-msg */}
                    {t.role === "user"
                      ? (
                        <>
                          <div className="st-ubub">
                            {t.ref && (
                              <span className="st-ubub__ref">
                                <span className="st-ubub__ref-dot" style={{ background: t.ref.color || "#5349E5" }} />
                                {(t.ref.name || t.ref.id || "").replace(/^\[DEMO\]\s*/i, "")}
                              </span>
                            )}
                            {/* Task 5: attachments the user sent — image thumbnails (click opens the
                                full CDN url) + PDF name-chips. Sourced from the durable payload
                                (chatTimeline maps payload.attachments onto the turn); replay-safe. */}
                            {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                              <div className="st-ubub__atts">
                                {t.attachments.map((a, ai) => (
                                  isImageAttachment(a.mime_type)
                                    ? (
                                      <a key={ai} className="st-ubub__att-img" href={a.url} target="_blank" rel="noopener noreferrer" title={a.name || "image"}>
                                        <img src={`${a.url}-/resize/320x/`} alt={a.name || "attachment"} />
                                      </a>
                                    )
                                    : (
                                      <a key={ai} className="st-ubub__att-doc" href={a.url} target="_blank" rel="noopener noreferrer">
                                        <span className="st-ubub__att-glyph" aria-hidden="true">PDF</span>
                                        <span className="st-ubub__att-name">{a.name || "document.pdf"}</span>
                                      </a>
                                    )
                                ))}
                              </div>
                            )}
                            {t.text && <span className="st-ubub__text">{t.text}</span>}
                          </div>
                          {/* Fix #4 — the failed-send affordance renders ANCHORED to its own user bubble
                              (inside this .st-turn--user), not always-last; auto-clears when the durable
                              echo drops the optimistic turn via mergeTimeline. */}
                          {t.status === "failed" && <SendNotice turn={t} onRetry={() => retrySend(t)} />}
                        </>
                      )
                      : (
                        // CHAT-3: settled assistant prose → markdown; falls back to plain text
                        // if the policy ever says no (defensive — settled assistant turns pass).
                        // S-A8: a copy button on each durable assistant turn (copies raw markdown).
                        <div className="st-msg">
                          {shouldMarkdownRender(t)
                            ? <AssistantMarkdown text={t.text} />
                            : t.text}
                          <CopyButton turn={t} />
                        </div>
                      )
                    }
                  </div>
                )
              })}
              {/* S4-2: thinking indicator — present from send until terminal. Driven by durable
                  stream + sentSinceTerminal flag, NOT the `sending` boolean.
                  Item A: during a pause-for-human (unresolved approval/ask_user card, no terminal)
                  the cycling indicator is REPLACED by the static directive "your turn" affordance.
                  Gated on {working && ...} (not `awaiting` alone) so it can only show inside an
                  in-flight turn this user actually started — same envelope isWorking enforces. */}
              {working && (
                awaiting ? (
                  <div className="st-turn" data-testid="st-awaiting">
                    <ThinkingIndicator
                      mode="awaiting"
                      label={awaitingNeedsCapability ? "Your turn — review above" : "Your turn — reply above"}
                    />
                  </div>
                ) : (
                  <div className="st-turn" data-testid="st-thinking">
                    <ThinkingIndicator />
                  </div>
                )
              )}
              {/* D2(b): the live typewriter lane is no longer a separate always-last concat — live
                  bubbles are FOLDED into convo.turns above (mergeTimeline, anchorSeq-ordered) so they
                  settle in place. The Stop suppression now lives in the allMessages memo (stopped → []). */}
              {/* Fix #4 — the failed-send affordance is no longer an always-last failedTurns.map block
                  (which floated to the bottom + never cleared). It renders anchored to its own user
                  bubble in the loop above (<SendNotice/>) and auto-clears on the durable echo. */}
            </div>
            {/* C13: .st-composer-wrap owns padding in non-empty state */}
            <div className="st-composer-wrap">
              <div className="st-composer-col">
                <SuggestionDock defaultOpen={true} items={dockItems} onPick={onPickSuggest} onExpand={onDockExpand} libraryHref={libraryHref} onLibraryNav={onLibraryNav} />
                {composerEl}
              </div>
            </div>
          </>
        )}
      </main>

      {/* Chat privacy 3e — the header icon opens the SAME modal component the nav uses (one component;
          it renders null when closed, so only one modal node is ever in the DOM). */}
      <ChatVisibilityModal
        open={visibilityModalOpen}
        mode="chat"
        chat={activeChatMeta}
        companySlug={companySlug}
        onClose={() => setVisibilityModalOpen(false)}
      />
    </div>
  )
}

// Chat privacy 3e — the slim-header visibility glyph (lock=private / people=shared / building=company).
// A tiny inline SVG so the header has no external icon dependency.
function HeaderVisibilityIcon({ name, size = 16 }) {
  const base = {
    width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
    strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true",
  }
  const paths = {
    lock: (<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>),
    people: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    building: (<><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01" /></>),
  }
  return <svg {...base}>{paths[name] || null}</svg>
}
